import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogoWithText } from '../components/Logo';
import { ArrowRight, List, X, Target, Globe, Lightbulb } from 'phosphor-react';
import './LandingPage.css';

export default function AboutPage() {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const pillars = [
    { icon: Target, title: 'Our Mission', body: 'Empower schools across Africa with technology that simplifies management and enhances educational quality.' },
    { icon: Globe, title: 'Our Vision', body: 'A world where every school, regardless of size, has access to enterprise-grade management tools.' },
    { icon: Lightbulb, title: 'Our Values', body: 'Transparency, accessibility, security, and student-centred design guide every decision we make.' },
  ];

  const milestones = [
    { year: '2023', title: 'Founded', body: 'Campusio was created to address school management challenges in the African education sector.' },
    { year: '2024', title: 'Growth', body: 'Expanded to include transport, hostel, and advanced accounting modules.' },
    { year: '2025', title: 'Scale', body: 'Now serving 50+ schools with 50,000+ active users across West Africa.' },
    { year: '2026', title: 'Innovation', body: 'Deeper analytics, offline-first mobile apps, and parent communication tools.' },
  ];

  const reasons = [
    { title: 'Designed for Africa', body: 'Built for local currencies, regulations, and workflows — not retrofitted from a Western product.' },
    { title: 'Affordable Pricing', body: 'Enterprise features at a fraction of international SaaS costs, accessible to all school sizes.' },
    { title: 'Local Support', body: 'Our support team understands your context and responds in your timezone.' },
    { title: 'Secure & Reliable', body: 'End-to-end encryption and 99.9% uptime SLA. Your data never leaves your control.' },
    { title: 'Continuously Evolving', body: 'New features shipped monthly, directly shaped by feedback from our school partners.' },
    { title: 'Community First', body: 'Training, documentation, and a growing network of school administrators across the region.' },
  ];

  const navLinks = [['/', 'Home'], ['/features', 'Features'], ['/documentation', 'Docs'], ['/contact', 'Contact']];

  return (
    <div style={{ background: 'var(--land-white)', color: 'var(--land-text)', fontFamily: "'IBM Plex Sans', sans-serif" }}>

      {/* Nav */}
      <nav className="lp-nav" style={{ background: 'rgba(255,255,255,0.97)', borderBottom: '1px solid var(--land-border)' }}>
        <div className="lp-nav-inner">
          <button onClick={() => navigate('/')} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
            <LogoWithText size={30} />
          </button>
          <div className="lp-nav-links">
            {navLinks.map(([path, label]) => (
              <button key={path} className="lp-nav-link" onClick={() => navigate(path)}>{label}</button>
            ))}
          </div>
          <div className="lp-nav-actions">
            <button className="lp-btn-ghost" onClick={() => navigate('/login')}>Sign In</button>
            <button className="lp-btn-primary" onClick={() => navigate('/contact')}>
              Request a Demo <ArrowRight size={15} weight="bold" />
            </button>
          </div>
          <button onClick={() => setMenuOpen(true)} className="lp-hamburger" style={{ display: 'none', background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
            <List size={26} color="var(--land-text)" />
          </button>
        </div>
      </nav>

      {menuOpen && (
        <div className="lp-mobile-menu">
          <button className="lp-mobile-close" onClick={() => setMenuOpen(false)}><X size={20} /></button>
          {navLinks.map(([path, label]) => (
            <button key={path} className="lp-mobile-link" onClick={() => { navigate(path); setMenuOpen(false); }}>{label}</button>
          ))}
          <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 10, paddingTop: 24, borderTop: '1px solid var(--land-border)' }}>
            <button className="lp-btn-outline-lg" style={{ justifyContent: 'center' }} onClick={() => { navigate('/login'); setMenuOpen(false); }}>Sign In</button>
            <button className="lp-btn-primary-lg" style={{ justifyContent: 'center' }} onClick={() => { navigate('/contact'); setMenuOpen(false); }}>Request a Demo</button>
          </div>
        </div>
      )}

      {/* Hero */}
      <section style={{ paddingTop: 120, paddingBottom: 64, textAlign: 'center', background: 'var(--land-off)', borderBottom: '1px solid var(--land-border)' }}>
        <div style={{ maxWidth: 640, margin: '0 auto', padding: '0 24px' }}>
          <p className="lp-section-label" style={{ justifyContent: 'center', display: 'flex' }}>About Us</p>
          <h1 style={{ fontSize: 'clamp(32px,5vw,52px)', fontWeight: 800, color: 'var(--land-text)', letterSpacing: '-0.03em', fontFamily: 'Manrope, sans-serif', marginBottom: 16, lineHeight: 1.1 }}>
            Built for African schools, by people who understand them.
          </h1>
          <p style={{ fontSize: 18, color: 'var(--land-muted)', lineHeight: 1.65 }}>
            Campusio is a complete school management platform purpose-built for the needs of schools across Ghana and West Africa.
          </p>
        </div>
      </section>

      {/* Mission / Vision / Values */}
      <section style={{ maxWidth: 1100, margin: '0 auto', padding: '80px 24px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24 }}>
          {pillars.map(({ icon: Icon, title, body }) => (
            <div key={title} style={{ padding: 32, background: 'var(--land-stone)', borderRadius: 14, border: '1px solid var(--land-border)' }}>
              <div style={{ width: 44, height: 44, background: 'var(--land-forest)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', marginBottom: 20 }}>
                <Icon size={22} weight="duotone" />
              </div>
              <h3 style={{ fontSize: 18, fontWeight: 700, color: 'var(--land-text)', fontFamily: 'Manrope, sans-serif', marginBottom: 10 }}>{title}</h3>
              <p style={{ fontSize: 15, color: 'var(--land-muted)', lineHeight: 1.65 }}>{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Journey */}
      <section style={{ background: 'var(--land-off)', borderTop: '1px solid var(--land-border)', borderBottom: '1px solid var(--land-border)' }}>
        <div style={{ maxWidth: 800, margin: '0 auto', padding: '80px 24px' }}>
          <p className="lp-section-label" style={{ marginBottom: 8 }}>Timeline</p>
          <h2 style={{ fontSize: 'clamp(26px,4vw,38px)', fontWeight: 800, color: 'var(--land-text)', fontFamily: 'Manrope, sans-serif', marginBottom: 56, letterSpacing: '-0.02em' }}>Our journey so far</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {milestones.map(({ year, title, body }, i) => (
              <div key={year} style={{ display: 'flex', gap: 32, paddingBottom: i < milestones.length - 1 ? 40 : 0 }}>
                <div style={{ flexShrink: 0, width: 64, paddingTop: 4 }}>
                  <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--land-forest)', fontFamily: 'Manrope, sans-serif' }}>{year}</div>
                </div>
                <div style={{ flex: 1, paddingBottom: 40, borderBottom: i < milestones.length - 1 ? '1px solid var(--land-border)' : 'none' }}>
                  <div style={{ fontWeight: 700, fontSize: 17, color: 'var(--land-text)', marginBottom: 6 }}>{title}</div>
                  <div style={{ fontSize: 15, color: 'var(--land-muted)', lineHeight: 1.65 }}>{body}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Campusio */}
      <section style={{ maxWidth: 1100, margin: '0 auto', padding: '80px 24px' }}>
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <p className="lp-section-label" style={{ justifyContent: 'center', display: 'flex' }}>Why Us</p>
          <h2 style={{ fontSize: 'clamp(26px,4vw,38px)', fontWeight: 800, color: 'var(--land-text)', fontFamily: 'Manrope, sans-serif', letterSpacing: '-0.02em', marginBottom: 16 }}>Why schools choose Campusio</h2>
          <p style={{ fontSize: 17, color: 'var(--land-muted)', maxWidth: 520, margin: '0 auto', lineHeight: 1.65 }}>Every feature is built with African educators in mind — not adapted from somewhere else.</p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20 }}>
          {reasons.map(({ title, body }) => (
            <div key={title} style={{ padding: '24px 28px', background: 'var(--land-stone)', borderRadius: 12, border: '1px solid var(--land-border)' }}>
              <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--land-text)', marginBottom: 8 }}>{title}</div>
              <div style={{ fontSize: 14, color: 'var(--land-muted)', lineHeight: 1.65 }}>{body}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <div className="lp-cta-band">
        <h2 className="lp-cta-h2">Ready to modernize your school?</h2>
        <p className="lp-cta-sub">Join the schools across Ghana and West Africa running on Campusio.</p>
        <button className="lp-btn-cta" onClick={() => navigate('/contact')}>
          Request a Demo <ArrowRight size={18} weight="bold" />
        </button>
      </div>

      {/* Footer */}
      <div style={{ borderTop: '1px solid var(--land-border)', padding: '24px 32px', textAlign: 'center', background: 'var(--land-off)' }}>
        <button onClick={() => navigate('/')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--land-muted)' }}>
          &larr; Back to Campusio
        </button>
      </div>
    </div>
  );
}
